import axios, { type AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthenticatedAxios } from "./authentication";
import { getFeatureFlagsFromCache } from "./use-config";

export type AccessLevel = "FULL" | "DEMO" | "BLOCKED";
export type LeadCreationStatus = "NOT_CREATED" | "CREATED" | "DONE";

export interface UserInfo {
  accessLevel: AccessLevel;
  fullAccessNotified: boolean;
  leadCreationStatus: LeadCreationStatus | null;
}

export type UserInfoStatus = "row" | "missing" | "loading" | "error";

export type UserInfoState = {
  userInfo: UserInfo | undefined;
  status: UserInfoStatus;
  isLoading: boolean;
  error: Error | undefined;
  updateFullAccessNotified: (value: boolean) => Promise<void>;
  refresh: () => Promise<void>;
};

function isMissingRowError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) && (error as AxiosError).response?.status === 404
  );
}

function triggerLeadCreation() {
  if (!getFeatureFlagsFromCache().cdpLeadCreation) return;
  try {
    const cdcUserRaw = localStorage.getItem("cdcUser");
    if (!cdcUserRaw) {
      console.warn(
        "No cdcUser in localStorage, skipping lead creation trigger",
      );
      return;
    }

    const cdcUser = JSON.parse(cdcUserRaw);
    const { axios } = getAuthenticatedAxios();

    axios
      .post("/v0/lead-creation", {
        profile: {
          email: cdcUser.email ?? "",
          firstName: cdcUser.firstName ?? "",
          lastName: cdcUser.lastName ?? "",
          company: cdcUser.company ?? "",
          country: cdcUser.country ?? "",
          city: cdcUser.city ?? "",
          address: cdcUser.address ?? "",
          zip: cdcUser.zip ?? "",
          extData: {
            work: {
              cov_industry: cdcUser.extData?.work?.cov_industry ?? "",
              cov_job_title: cdcUser.extData?.work?.cov_job_title ?? "",
            },
            custom: {
              phone: cdcUser.extData?.custom?.phone ?? "",
            },
          },
        },
      })
      .catch((err: unknown) => {
        console.error("Failed to trigger lead creation", err);
      });
  } catch (err) {
    console.error("Error preparing lead creation trigger", err);
  }
}

export function useUserInfo(shouldLoad: boolean): UserInfoState {
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [overrideUserInfo, setOverrideUserInfo] = useState<UserInfo>();
  const [status, setStatus] = useState<UserInfoStatus>(
    shouldLoad ? "loading" : "missing",
  );
  const [error, setError] = useState<Error>();
  const leadCreationTriggered = useRef(false);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(undefined);
    const { axios } = getAuthenticatedAxios();
    try {
      const res = await axios.get<UserInfo>("/v0/user-info", {
        params: { cache: Date.now() },
      });
      setUserInfo(res.data);
      setStatus("row");
    } catch (err) {
      if (isMissingRowError(err)) {
        setUserInfo(undefined);
        setStatus("missing");
        return;
      }
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (!shouldLoad) {
      setStatus("missing");
      return;
    }
    void load();
  }, [shouldLoad, load]);

  useEffect(() => {
    if (
      userInfo &&
      status === "row" &&
      userInfo.accessLevel === "DEMO" &&
      userInfo.leadCreationStatus === "NOT_CREATED" &&
      !leadCreationTriggered.current
    ) {
      leadCreationTriggered.current = true;
      triggerLeadCreation();
    }
  }, [userInfo, status]);

  const updateFullAccessNotified = useCallback(
    async (value: boolean) => {
      if (userInfo) {
        setOverrideUserInfo({ ...userInfo, fullAccessNotified: value });
      }
      const { axios } = getAuthenticatedAxios();
      await axios.put("/v0/user-info", { fullAccessNotified: value });
    },
    [userInfo],
  );

  return {
    userInfo: overrideUserInfo ?? userInfo,
    status,
    isLoading: status === "loading",
    error,
    updateFullAccessNotified,
    refresh: load,
  };
}
